import React, { useState } from 'react';
import { Alert, Button, Form, Tab, Tabs } from 'react-bootstrap';
import { AssetMaintenanceDepartureGuard } from './AssetMaintenanceDepartureGuard';
import { AssetPmTaskEditor } from './AssetPmTaskEditor';

export function AssetPmChecklistForm({ detail, kind, onSave, onCancel }) {
    const [date, setDate] = useState(detail.checklist?.maintenanceStartDate || '');
    const [frequency, setFrequency] = useState('Daily');
    const [selectedFrequencies, setSelectedFrequencies] = useState(() =>
        detail.schedules
            .filter((schedule) => schedule.isEnabled ?? !!detail.checklist)
            .map((schedule) => schedule.frequency)
    );
    const [tasks, setTasks] = useState(() =>
        Object.fromEntries(
            detail.schedules.map((schedule) => [
                schedule.frequency,
                schedule.tasks.map((task) => ({ ...task, attachmentIds: [...task.attachmentIds] })),
            ])
        )
    );
    const [dirty, setDirty] = useState(false);
    const [saved, setSaved] = useState(detail);
    const [busy, setBusy] = useState(false);
    const [message, setMessage] = useState('');
    const [error, setError] = useState(null);
    const [photoStates, setPhotoStates] = useState({});
    const photosBlocked = Object.values(photoStates).some((state) => state.blocked);
    const photosPending = Object.values(photoStates).some((state) => state.pending);
    const [photoReset, setPhotoReset] = useState(0);

    function change(seq, description) {
        setTasks((previous) => ({
            ...previous,
            [frequency]: previous[frequency].map((task) =>
                task.seq === seq ? { ...task, description } : task
            ),
        }));
        setDirty(true);
        setMessage('Unsaved changes');
    }

    async function save(isActive) {
        if (photosBlocked || busy || error?.latest) return;

        setBusy(true);
        setError(null);
        setMessage('Saving…');
        try {
            const result = await onSave({
                maintenanceStartDate: date || null,
                isActive,
                revision: saved.checklist?.revision ?? null,
                frequencies: tasks,
                selectedFrequencies,
            });
            setSaved(result);
            setDirty(false);
            setMessage(
                isActive ? 'Checklist activated and saved.' : 'Draft saved. Scheduling is paused.'
            );
        } catch (failure) {
            setError(failure);
            setMessage('Changes are still in this form.');
        } finally {
            setBusy(false);
        }
    }

    function reloadLatest() {
        const latest = error.latest;
        setPhotoReset((value) => value + 1);
        setPhotoStates({});
        setSaved(latest);
        setDirty(false);
        setDate(latest.checklist?.maintenanceStartDate || '');
        setSelectedFrequencies(
            latest.schedules.filter((schedule) => schedule.isEnabled).map((schedule) => schedule.frequency)
        );
        setTasks(
            Object.fromEntries(
                latest.schedules.map((schedule) => [schedule.frequency, schedule.tasks])
            )
        );
        setError(null);
        setMessage('Latest saved checklist loaded.');
    }

    return (
        <Form onSubmit={(event) => event.preventDefault()}>
            <AssetMaintenanceDepartureGuard
                blocked={dirty || photosBlocked || busy}
                busy={busy || photosPending}
            />
            <h2>
                {detail.asset.name ||
                    detail.asset.machine ||
                    detail.asset.testerNo ||
                    detail.asset.jigFixNo}
            </h2>
            <Form.Group className="mb-3">
                <Form.Label htmlFor="pm-start">Maintenance start date</Form.Label>
                <Form.Control
                    id="pm-start"
                    type="date"
                    className="asset-pm-start-date"
                    value={date}
                    disabled={busy}
                    onChange={(event) => {
                        setDate(event.target.value);
                        setDirty(true);
                        setMessage('Unsaved changes');
                    }}
                />
                <Form.Text>
                    Daily starts on this date. Other selected frequencies start after one interval.
                    Existing maintenance records keep their original dates and tasks.
                </Form.Text>
            </Form.Group>
            <fieldset className="mb-3" disabled={busy || !!error?.latest}>
                <legend className="fs-6">Scheduled frequencies</legend>
                {detail.schedules.map((schedule) => (
                    <Form.Check
                        inline
                        key={schedule.frequency}
                        id={'pm-enabled-' + schedule.frequency}
                        label={schedule.frequency}
                        checked={selectedFrequencies.includes(schedule.frequency)}
                        onChange={(event) => {
                            const enabled = event.target.checked;
                            setSelectedFrequencies((previous) => enabled
                                ? [...previous, schedule.frequency]
                                : previous.filter((value) => value !== schedule.frequency)
                            );
                            setDirty(true);
                            setMessage('Unsaved changes');
                        }}
                    />
                ))}
                <Form.Text className="d-block">
                    Only selected frequencies generate maintenance records. All five task lists remain editable.
                </Form.Text>
            </fieldset>
            {error && (
                <Alert variant="danger">
                    {error.message}
                    {Object.entries(error.fields || {}).map(([field, text]) => (
                        <div key={field}>
                            {field}: {text}
                        </div>
                    ))}
                    {error.latest && (
                        <Button variant="outline-danger" onClick={reloadLatest}>
                            Load latest saved checklist
                        </Button>
                    )}
                </Alert>
            )}
            <Tabs
                activeKey={frequency}
                onSelect={(value) => {
                    if (!photosBlocked && !busy) setFrequency(value);
                }}
                className="mb-3"
            >
                {detail.schedules.map((schedule) => (
                    <Tab
                        key={schedule.frequency}
                        eventKey={schedule.frequency}
                        title={schedule.frequency}
                    />
                ))}
            </Tabs>
            <AssetPmTaskEditor
                key={frequency + photoReset}
                tasks={tasks[frequency]}
                onChange={change}
                disabled={busy || !!error?.latest}
                kind={kind}
                revision={saved.checklist?.revision}
                onPhotoConflict={setError}
                scheduleId={
                    saved.schedules.find((schedule) => schedule.frequency === frequency)?.id
                }
                onPhotoState={(seq, blocked, pending) =>
                    setPhotoStates((previous) => ({
                        ...previous,
                        [frequency + seq]: { blocked, pending },
                    }))
                }
                onPhotos={(seq, ids, metadata) => {
                    setTasks((previous) => ({
                        ...previous,
                        [frequency]: previous[frequency].map((task) =>
                            task.seq === seq ? { ...task, attachmentIds: ids } : task
                        ),
                    }));
                    if (metadata?.parentRevision != null)
                        setSaved((previous) => ({
                            ...previous,
                            checklist: { ...previous.checklist, revision: metadata.parentRevision },
                        }));
                    setDirty(true);
                    setMessage('Unsaved changes');
                }}
            />
            <div role="status" aria-live="polite" className="mb-2">
                {message}
            </div>
            <Button
                disabled={busy || photosBlocked || !!error?.latest}
                onClick={() => save(false)}
                className="me-2"
            >
                Save draft
            </Button>
            <Button
                disabled={busy || photosBlocked || !!error?.latest}
                variant="success"
                title={selectedFrequencies.length ? undefined : 'Select a scheduled frequency before activation.'}
                onClick={() => save(true)}
                className="me-2"
            >
                Activate checklist
            </Button>
            {onCancel && (
                <Button disabled={busy} variant="secondary" onClick={onCancel}>
                    Back to checklist list
                </Button>
            )}
        </Form>
    );
}
